import { Outlet } from 'react-router';
import { useDeconnexion, useSession } from '../features/session/session';
import { Bouton } from '../shared/ui/Bouton';
import { LienDeNavigation } from '../shared/ui/LienDeNavigation';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE CADRE DES ÉCRANS PERSISTANTS — ET LE TABLEAU N'EN FAIT PAS PARTIE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Cinq écrans partagent la même barre : l'arrivée, l'historique, l'annuaire, l'espace de
 * fichiers et les réglages. **Le tableau d'un cours, lui, n'a pas de barre du tout** —
 * « le tuteur et l'élève passent **le cours entier dans un seul écran**, le tableau en
 * plein écran. **Ils n'y naviguent pas, ils y travaillent** » — et c'est pour cela que ce
 * cadre est une route de mise en page et non un composant que chaque page appellerait :
 * **une page qui doit se souvenir de porter le cadre est une page qui l'oubliera**, et le
 * jour où elle l'oublie, l'écran perd sa navigation sans que rien ne rougisse.
 *
 * **CE CADRE NE DÉCIDE D'AUCUN DROIT.** `PortailDeSession` a déjà refusé l'entrée à qui
 * n'a pas de session ; ce qui reste ici est du placement. Une seconde garde donnerait
 * deux endroits où lire la même règle, et c'est ainsi qu'elles divergent.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LA BARRE NE PORTE PAS, ET CHAQUE ABSENCE EST UNE RÈGLE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucun total de temps de cours** (F-46) : « le total ne s'affiche nulle part
 *   ailleurs — deux emplacements pour une même valeur finiraient par en montrer deux ».
 * - **Aucun compteur de nouveautés** (F-54) : « **rien ne dit ce qui est neuf depuis la
 *   dernière visite**, sauf le chat, qui porte le rang du dernier message lu ». Le produit
 *   ne conserve nulle part qu'une personne a vu un écran, donc il ne peut rien en dire.
 * - **Aucune surface d'aide ni de contact** (Q483, Q489) : « la V1 n'en porte AUCUNE, ni
 *   page qui explique, ni moyen d'écrire, ni lien qui sort. Une personne qui a un problème
 *   passe par la structure qui lui a donné son accès, **hors du produit**. »
 * - **Aucun nom de structure** : sur un cours mixte, un élève d'une autre structure
 *   lirait un rattachement qui n'est pas le sien. La structure se lit dans les réglages,
 *   où elle est **la sienne**.
 */
export function CadreApplication() {
  const session = useSession();
  const depart = useDeconnexion();

  return (
    <div className="min-h-dvh bg-fond-travail">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-bord bg-fond-surface px-6 py-3">
        <span className="flex items-center gap-6">
          <span className="text-16 font-weight-800 tracking-marque text-texte-fort">
            SWYPE
          </span>
          <nav className="flex items-center gap-1">
            <LienDeNavigation vers="/arrivee">Accueil</LienDeNavigation>
            <LienDeNavigation vers="/historique">Historique</LienDeNavigation>
            <LienDeNavigation vers="/annuaire">Conversations</LienDeNavigation>
            <LienDeNavigation vers="/espace">Fichiers</LienDeNavigation>
            <LienDeNavigation vers="/reglages">Réglages</LienDeNavigation>
          </nav>
        </span>
        <span className="flex items-center gap-4">
          {/* **Le prénom seul, ici comme partout** : « le produit ne montre jamais d'un
              tiers plus que son prénom » — et il ne montre pas davantage du sien, ce qui
              évite qu'une capture d'écran de cours porte un nom de famille. */}
          <span className="text-14 text-texte-faible">
            {session.data?.compte.prenom ?? ''}
          </span>
          <Bouton
            variante="fantome"
            taille="petite"
            onClick={() => depart.mutate()}
            enCours={depart.isPending}
          >
            Se déconnecter
          </Bouton>
        </span>
      </header>

      <Outlet />
    </div>
  );
}
